// Unpublished Work © 2023-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import {PersistentHeader} from '@deere/persistent-header';
import Entry from 'Ui/components/entry/entry';
import LoadingWrapper from 'Ui/components/common/loading-wrapper';
import {MainNavBarWrapper} from 'Ui/components/main-navbar';
import MainContentBackground from 'Ui/components/main-content-background';
import PersistentHeaderChildWrapper from 'Ui/components/entry/persistent-header-child-wrapper';
import {getLicensedTurfFeatures} from 'Services/membership-service';
import {fetchEffectData} from 'Utils/react-utils';
import {replaceTranslationNames} from 'Utils/translation-utils';
import {MY_JOHN_DEERE} from 'Ui/constants/replace-constants';
import {openDialog} from 'Store/actions/dialogs';
import dialogTypes from 'Ui/components/common/dialog-types';
import {redirect} from 'Utils/redirect-utils';
import {getLocationHref} from 'Utils/window-location-utils';
import {PRO_GOLF_LICENSE_MANAGER_2} from 'Common/constants/isg-toggles';
import {
    PH_BASE_HOME_ROUTES,
    PH_BASE_FLEET_ROUTES,
    PH_BASE_LABOR_ROUTES,
    PH_BASE_FLEET_MAINTENANCE_ROUTES,
    PH_BASE_PART_ROUTES,
    CONTRIBUTION_PRODUCT_IDS
} from 'Ui/constants/routes/base-route-config';

function callOpenErrorDialog(openErrorDialog, translations, location, setLoading, setSetShowErrorDialog) {
    openErrorDialog({
        messageText: replaceTranslationNames(translations.ONLINK_USER_CREDIENTIALS_NO_LONGER_VALID, {
            '0': MY_JOHN_DEERE
        }),
        buttonText: replaceTranslationNames(translations.ONLINK_MYJOHNDEERE_SIGNIN, {
            '0': MY_JOHN_DEERE
        }),
        onClickAction: () => {
            setLoading(true);
            setSetShowErrorDialog(false);
            redirect(`/api/ops-center-redirect?redirect=${location.pathname}`);
        }
    });
}

function arrayContainsPrefix(stringToCheck, arrayOfStrings) {
    return arrayOfStrings.some((string) => stringToCheck?.startsWith(string));
}

function getContributionProductId(pathName) {
    if (arrayContainsPrefix(pathName, PH_BASE_HOME_ROUTES)) {
        return CONTRIBUTION_PRODUCT_IDS.HOME;
    } else if (arrayContainsPrefix(pathName, PH_BASE_FLEET_ROUTES)) {
        return CONTRIBUTION_PRODUCT_IDS.FLEET;
    } else if (arrayContainsPrefix(pathName, PH_BASE_LABOR_ROUTES)) {
        return CONTRIBUTION_PRODUCT_IDS.LABOR;
    } else if (arrayContainsPrefix(pathName, PH_BASE_FLEET_MAINTENANCE_ROUTES)) {
        return CONTRIBUTION_PRODUCT_IDS.FLEET_MAINTENANCE;
    } else if (arrayContainsPrefix(pathName, PH_BASE_PART_ROUTES)) {
        return CONTRIBUTION_PRODUCT_IDS.PART;
    }
    return CONTRIBUTION_PRODUCT_IDS.COURSE_OPERATIONS;
}

function EntryComponent(props) {
    const {
        hasSubscription: initialHasSubscription,
        hasMyJDSession,
        isLicenseManagerEnabled,
        isStandalonePage,
        isWorkboardForm,
        location,
        mainNavBarProps,
        membership,
        openErrorDialog,
        translations,
        myJdId
    } = props;

    const contributionProductId = React.useMemo(
        () => getContributionProductId(location.pathname),
        [location.pathname]
    );
    const [loading, setLoading] = React.useState(true);
    const [licensedFeatures, setLicensedFeatures] = React.useState([]);
    const hasSubscription = isLicenseManagerEnabled ? licensedFeatures.length > 0 : Boolean(initialHasSubscription);
    const [showPersistentHeader, setShowPersistentHeader] = React.useState(hasMyJDSession && !isStandalonePage);
    const [showErrorDialog, setSetShowErrorDialog] = React.useState(false);

    const entryComponent = (
        <Entry
            hasSubscription={hasSubscription}
            isStandalonePage={isStandalonePage}
            isWorkboardForm={isWorkboardForm}
            licensedFeatures={licensedFeatures}
            loadPersistentHeader={showPersistentHeader}
            translations={translations}
        />
    );

    const page = showPersistentHeader ? (
        <PersistentHeader
            contributionProductId={contributionProductId}
            logoutRedirectUri={`${window.props.myjdRedirectLocation}/ph-logout`}
            render={(renderProps) => (
                <PersistentHeaderChildWrapper
                    {...renderProps}
                    membershipOrgId={membership.orgId}
                    translations={translations}
                >
                    {entryComponent}
                </PersistentHeaderChildWrapper>
            )}
            sourceAppUri={`${getLocationHref()}?source=opscenter`}
            url={window.props.persistentHeaderUrl}
            userId={myJdId}
        />
    ) : (
        <>
            {
                !isStandalonePage &&
                <>
                    <MainNavBarWrapper {...mainNavBarProps}/>
                    <MainContentBackground/>
                </>
            }
            {
                showErrorDialog ?
                    callOpenErrorDialog(
                        openErrorDialog,
                        translations,
                        location,
                        setLoading,
                        setSetShowErrorDialog
                    ) : entryComponent
            }
        </>
    );

    React.useEffect(() => {
        if (isLicenseManagerEnabled) {
            fetchEffectData(async (isMounted) => {
                setLoading(true);

                const {licensedFeatures: features} = await getLicensedTurfFeatures();

                if (isMounted()) {
                    setLicensedFeatures(features);
                    setShowPersistentHeader(hasMyJDSession && !isStandalonePage);
                    setSetShowErrorDialog(!hasMyJDSession && membership.isMigrated);
                    setLoading(false);
                }
            });
        } else {
            setShowPersistentHeader(hasMyJDSession && !isStandalonePage);
            setSetShowErrorDialog(!hasMyJDSession && membership.isMigrated);
            setLoading(false);
        }
    }, [hasMyJDSession, isLicenseManagerEnabled, isStandalonePage, membership.isMigrated]);

    return (
        <LoadingWrapper
            className='dashboard-loading-icon'
            loading={loading}
            size='50px'
        >
            {page}
        </LoadingWrapper>
    );
}

EntryComponent.propTypes = {
    hasMyJDSession: PropTypes.bool,
    hasSubscription: PropTypes.bool,
    isLicenseManagerEnabled: PropTypes.bool,
    isStandalonePage: PropTypes.bool,
    isWorkboardForm: PropTypes.bool,
    location: PropTypes.location,
    mainNavBarProps: PropTypes.object,
    membership: PropTypes.membership,
    myJdId: PropTypes.string,
    openErrorDialog: PropTypes.func,
    setMyJdPermissions: PropTypes.func,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        membership: state.membership,
        hasMyJDSession: state.account.hasMyJDSession,
        isLicenseManagerEnabled: Boolean(state.account.isgToggles?.hasOwnProperty(PRO_GOLF_LICENSE_MANAGER_2)),
        myJdId: state.account.myJdId
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        openErrorDialog(props) {
            dispatch(openDialog(dialogTypes.ERROR_DIALOG, props));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(EntryComponent);
